import type { SSEMessage } from '@/_shared/types';

// ---------------------------------------------------------------------------
// Message grouping
// ---------------------------------------------------------------------------

export interface ActivityItem {
  toolUse: {
    name: string;
    friendlyName: string;
    category: string;
    action: string;
    args: unknown;
    id: string;
    summary?: string;
    groupedTitle?: string;
    detail?: { type: 'code'; content: string; language: string } | null;
  };
  toolResult?: { id: string; content: string };
}

export type GroupedMessage =
  | { kind: 'single'; message: SSEMessage; index: number }
  | { kind: 'activity'; items: ActivityItem[]; startIndex: number; context?: string };

export function groupMessages(messages: SSEMessage[]): GroupedMessage[] {
  const result: GroupedMessage[] = [];

  // Track skipped mcp__ui__ tool_use IDs so we also skip their results
  const skippedIds = new Set<string>();
  let currentActivity: ActivityItem[] | null = null;
  let activityStartIndex = 0;

  // Capture the last assistant text to attach as context on the next activity group
  let pendingContext: string | null = null;
  let activityContext: string | null = null;

  function flushActivity(): void {
    if (currentActivity && currentActivity.length > 0) {
      result.push({
        kind: 'activity',
        items: currentActivity,
        startIndex: activityStartIndex,
        ...(activityContext ? { context: activityContext } : {}),
      });
    }
    currentActivity = null;
    activityContext = null;
  }

  for (let i = 0; i < messages.length; i++) {
    const msg = messages[i]!;

    if (msg.type === 'tool_use') {
      // Filter mcp__ui__* tools
      if (msg.name.startsWith('mcp__ui__')) {
        skippedIds.add(msg.id);
        continue;
      }

      // Start or continue activity group
      if (!currentActivity) {
        currentActivity = [];
        activityStartIndex = i;
        // Capture the pending assistant context for this new group
        activityContext = pendingContext;
        pendingContext = null;
      }

      currentActivity.push({
        toolUse: {
          name: msg.name,
          friendlyName: msg.friendlyName,
          category: msg.category,
          action: msg.action,
          args: msg.args,
          id: msg.id,
          summary: msg.summary,
          groupedTitle: msg.groupedTitle,
          detail: msg.detail,
        },
      });
      continue;
    }

    if (msg.type === 'tool_result') {
      // Skip results for filtered mcp__ui__ tools
      if (skippedIds.has(msg.id)) {
        continue;
      }

      // Try to pair with a tool_use in the current activity
      if (currentActivity) {
        const match = currentActivity.find((item) => item.toolUse.id === msg.id);
        if (match) {
          match.toolResult = { id: msg.id, content: msg.content };
          continue;
        }
      }

      // Orphan tool_result — skip silently
      continue;
    }

    // Assistant messages are transient — track the latest one as pending context
    // for the next activity group. They don't break groups.
    // Only update if non-empty (empty messages are streaming artifacts).
    if (msg.type === 'assistant') {
      if (msg.content) pendingContext = msg.content;
      continue;
    }

    // Result messages have no visual representation — skip without breaking groups.
    if (msg.type === 'result') {
      continue;
    }

    // Visible non-tool message breaks the activity group
    flushActivity();
    pendingContext = null;
    result.push({ kind: 'single', message: msg, index: i });
  }

  // Flush any trailing activity group
  flushActivity();

  return result;
}

// ---------------------------------------------------------------------------
// Active state detection
// ---------------------------------------------------------------------------

export function isToolActive(messages: SSEMessage[]): boolean {
  for (let i = messages.length - 1; i >= 0; i--) {
    const msg = messages[i]!;
    // Skip filtered mcp__ui__ tool_use and their results
    if (msg.type === 'tool_use' && msg.name.startsWith('mcp__ui__')) continue;
    if (msg.type === 'tool_result') continue;
    return msg.type === 'tool_use';
  }
  return false;
}
